import { Theme, css } from '@emotion/react';

import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

// import { useRouter } from 'next/router';
import { useCallback } from 'react';
import { useQuery } from 'react-query';

import { Spacing, Typography } from 'chatie-design-system';
import { Icon, VerticalEllipsis } from 'chatie-design-system/icon';

import Avatar from '@components/common/Avatar';

import { fetchFollowingUsers } from '@api/user';
import { Platform, QUERY_KEYS } from '@constants';
import { useGlobalContext } from '@contexts/global';
import { ISimpleUser } from '@model';
import { useMe, useUserMarketItemInventories } from '@utils/hook';
import { useWebView } from '@utils/webview';

const followingUserListCSS = (theme: Theme) => css`
  white-space: nowrap;
  overflow: auto;
  padding: ${Spacing.xxsmall}px ${Spacing.small}px ${Spacing.medium}px;
  height: 98px;

  .following-user {
    display: inline-block;
    width: 64px;
    padding-top: ${Spacing.medium}px;
    text-align: center;

    > div {
      margin: 0 auto;
    }

    small {
      display: block;
      margin-top: ${Spacing.small}px;
      ${Typography.body12}
    }

    &--loading {
      color: ${theme.var.gray500};
    }
  }

  .view-more {
    display: inline-block;
  }

  > * + * {
    margin-left: ${Spacing.xxsmall}px;
  }
`;

const emptyFollowingUserListCSS = (theme: Theme) => css`
  display: flex;
  height: 98px;
  align-items: center;
  justify-content: center;

  p {
    ${Typography.body14}
    color: ${theme.var.gray400};
  }
`;

const FollowingUserList = () => {
  const { t } = useTranslation();
  const { data: me } = useMe();

  const router = useRouter();
  const { platform } = useGlobalContext();
  const { web } = useWebView();

  const followingUserQuery = useQuery(
    [QUERY_KEYS.FOLLOWING.FEED_USER_LIST, me?.id],
    () => fetchFollowingUsers(me!.id),
    { enabled: !!me, suspense: false }
  );

  useUserMarketItemInventories(followingUserQuery.data?.items ?? []);

  const handleGoToUserPage = useCallback((user: ISimpleUser) => {
    const url = `/users/${user.id}`;
    if (platform === Platform.web) {
      router.push(url);
    } else {
      web.openPage(url);
    }
  }, []);

  if (!me) {
    return null;
  }

  if (followingUserQuery.isFetched) {
    if (followingUserQuery.data && followingUserQuery.data.items.length > 0) {
      return (
        <section css={followingUserListCSS}>
          {followingUserQuery.data?.items.map((user) => (
            <div
              className="following-user"
              key={`following-user-${user.id}`}
              onClick={() => handleGoToUserPage(user)}
            >
              <Avatar {...user} />
              <small className="ellipsis">{user.nickname}</small>
            </div>
          ))}
          {followingUserQuery.data.hasNext && (
            <div
              className="following-user"
              onClick={() => web.openPage(`/users/${me.id}/followings`)}
            >
              <Icon icon={VerticalEllipsis} />
              <small className="ellipsis">{t('더보기')}</small>
            </div>
          )}
        </section>
      );
    }

    return (
      <section css={emptyFollowingUserListCSS}>
        <p>{t('팔로잉하는 유저가 없습니다.')}</p>
      </section>
    );
  }

  return (
    <section css={followingUserListCSS}>
      {[...Array(10).keys()].map((i) => (
        <div
          className="following-user following-user--loading"
          key={`empty-avatar-${i}`}
        >
          <Avatar />
          <small>...</small>
        </div>
      ))}
    </section>
  );
};

export default FollowingUserList;
